import type { CSSProperties, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";

import { CloseIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { cn } from "@/lib/utils";

type DesktopPanelStyle = CSSProperties & Record<"--select-trigger-width", string>;

interface SearchableSelectPortalsProps {
  title: ReactNode;
  icon?: AppIcon;
  desktopPanelRef: RefObject<HTMLDivElement | null>;
  modalRef: RefObject<HTMLDivElement | null>;
  desktopInputRef: RefObject<HTMLInputElement | null>;
  mobileInputRef: RefObject<HTMLInputElement | null>;
  desktopOptionsRef: RefObject<HTMLUListElement | null>;
  mobileOptionsRef: RefObject<HTMLUListElement | null>;
  desktopPanelStyle: DesktopPanelStyle | null;
  desktopPanelClassName?: string;
  desktopOptionsClassName: string;
  mobilePanelClassName?: string;
  mobileOptionsClassName: string;
  renderDesktopInputInPanel: boolean;
  renderMobileInputInPanel: boolean;
  close: () => void;
  renderInput?: (ref: RefObject<HTMLInputElement | null>, mode: "desktop" | "mobile") => ReactNode;
  renderOptions: (
    ref: RefObject<HTMLUListElement | null>,
    className: string,
    mode: "desktop" | "mobile",
  ) => ReactNode;
}

export function SearchableSelectPortals({
  title,
  icon,
  desktopPanelRef,
  modalRef,
  desktopInputRef,
  mobileInputRef,
  desktopOptionsRef,
  mobileOptionsRef,
  desktopPanelStyle,
  desktopPanelClassName,
  desktopOptionsClassName,
  mobilePanelClassName,
  mobileOptionsClassName,
  renderDesktopInputInPanel,
  renderMobileInputInPanel,
  close,
  renderInput,
  renderOptions,
}: SearchableSelectPortalsProps) {
  if (typeof document === "undefined") return null;
  const Icon = icon;

  return (
    <>
      {createPortal(
        <div
          ref={desktopPanelRef}
          style={
            desktopPanelStyle ?? {
              left: 0,
              top: 0,
              visibility: "hidden",
            }
          }
          className={cn(
            "hidden md:block fixed z-50 bg-popover border border-border rounded-lg shadow-xl overflow-hidden",
            desktopPanelClassName,
          )}
        >
          {renderDesktopInputInPanel ? renderInput?.(desktopInputRef, "desktop") : null}
          {renderOptions(desktopOptionsRef, desktopOptionsClassName, "desktop")}
        </div>,
        document.body,
      )}

      {createPortal(
        <div
          className="fixed inset-0 z-[70] md:hidden flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          onClick={close}
        >
          <div
            ref={modalRef}
            className={cn(
              "w-[min(calc(100vw_-_2rem),28rem)] max-h-[min(70svh,32rem)] bg-popover border border-border rounded-xl shadow-2xl overflow-hidden",
              mobilePanelClassName,
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-border">
              <div className="min-w-0 flex items-center gap-2 text-sm font-medium">
                {Icon ? <Icon className="text-[18px] text-muted-foreground shrink-0" /> : null}
                <span className="truncate">{title}</span>
              </div>
              <button
                type="button"
                onClick={close}
                className="size-11 sm:size-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <CloseIcon className="text-[18px]" />
              </button>
            </div>
            {renderMobileInputInPanel ? renderInput?.(mobileInputRef, "mobile") : null}
            {renderOptions(mobileOptionsRef, mobileOptionsClassName, "mobile")}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
